import { Component, Optional, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { InboxService, ParticipantService, SubjectParticipant } from '@galaxy/conversation/core';
import { TranslateService } from '@ngx-translate/core';
import type { SalesContact } from '@vendasta/protos/atlas/v1/types';
import { ConversationChannel, GlobalParticipantType, Participant } from '@vendasta/conversation';
import { CountryCode, format } from 'libphonenumber-js';
import { Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { BusinessNavConfigService } from '../../config.service';
import { BusinessNavDataService } from '../../data.service';

@Component({
  selector: 'bc-sales-info-modal',
  templateUrl: './sales-info.modal.component.html',
  styleUrls: ['./sales-info.modal.component.scss'],
  standalone: false,
})
export class SalesInfoModalComponent {
  dialogRef = inject(MatDialogRef<SalesInfoModalComponent>);

  public headerText$: Observable<string>;
  public partnerId$: Observable<string>;
  public accountGroupId$: Observable<string>;
  public canAccessInbox$: Observable<boolean> = of(false);
  public accountHasInboxEnabled$: Observable<boolean>;

  subjectParticipants$ = combineLatest([this.configService.partnerId$, this.configService.accountGroupId$]).pipe(
    map(([partnerId, accountGroupId]) => [
      new SubjectParticipant({
        internalParticipantId: accountGroupId,
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
      }),
      new SubjectParticipant({
        internalParticipantId: partnerId,
        participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
      }),
    ]),
  );

  currentIAMParticipant$: Observable<Participant>;
  readonly conversationChannel = ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;

  salespersonContact$: Observable<SalesContact> = this.dataService.salespersonContact$;
  salespersonPicture$: Observable<string> = this.dataService.salespersonPictureUrl$;

  constructor(
    private dataService: BusinessNavDataService,
    private t: TranslateService,
    private configService: BusinessNavConfigService,
    @Optional() private inboxService: InboxService,
    @Optional() private participantService: ParticipantService,
  ) {
    this.headerText$ = this.dataService.partnerName$.pipe(
      switchMap((partnerName) => {
        return this.t.stream('COMMON.CONTACT_SALESPERSON', { salesPersonName: partnerName });
      }),
    );
    this.initInboxAccess();
  }

  /**
   * Initiate access rules specifically for Inbox Chat Modal Button in Business App.
   */
  initInboxAccess(): void {
    if (!this.inboxService || !this.participantService) {
      return;
    }

    this.currentIAMParticipant$ = this.participantService?.buildIAMUserParticipant() as Observable<Participant>;

    this.accountHasInboxEnabled$ = this.dataService.navigationItems$.pipe(
      map((navItems) => !!navItems.find((navItem) => navItem?.sideNavigationLink?.navigationId === 'nav-inbox')),
    );

    this.canAccessInbox$ = combineLatest([
      this.inboxService.allowSmbChatWithPartner$,
      this.accountHasInboxEnabled$,
    ]).pipe(
      map(
        ([allowSmbChat, accountHasInboxEnabled]) =>
          allowSmbChat && accountHasInboxEnabled,
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  formatPhoneNumber(phoneNumber: string | number, countryCode?: string): string {
    if (!phoneNumber) {
      return '';
    }

    const numberString = phoneNumber.toString();
    if (numberString === '') {
      return '';
    }
    const formatting = 'NATIONAL';
    if (countryCode as CountryCode) {
      const newCountryCode = countryCode as CountryCode;
      return format(numberString, newCountryCode, formatting);
    }
    return numberString;
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
